import { IProductDetail, User } from '@/api/types/public';
import { ProviderType, TrackingEvent } from '../types';
import { env } from '@/utils/env';
import { resolveTrackingEventId } from '../utils/eventIdGenerator';
import { toOpenAiMinorUnits } from './openAiEventUtils';

export function goodDetailViewAdapter(
  data: {
    product: IProductDetail;
    finalPrice: number;
    baseFinalPrice: string;
    userInfo: User;
    ttqEventId: string;
    currencyName: string;
    searchParams: {
      page?: string;
      search_index?: string;
    };
  },
  provider: ProviderType,
  eventId?: string,
): TrackingEvent | TrackingEvent[] | null {
  const { product, finalPrice, baseFinalPrice, userInfo, currencyName, searchParams } = data;
  switch (provider) {
    case ProviderType.FACEBOOK:
      return {
        eventName: 'ViewContent',
        eventId,
        properties: {
          content_type: 'product',
          content_ids: product.shop_sku,
          is_new_customer: userInfo.first_order_at ? 2 : 1,
          first_order_at: userInfo.first_order_at,
        },
      };
    case ProviderType.CRITEO:
      return {
        eventName: 'viewItem',
        eventId,
        properties: {
          item: product.id,
        },
      };
    case ProviderType.BING:
      return {
        eventName: 'event',
        eventId,
        properties: {
          ecomm_prodid: product.id,
          ecomm_pagetype: 'product',
        },
      };
    case ProviderType.SNAPCHAT:
      return {
        eventName: 'VIEW_CONTENT',
        eventId,
        properties: {
          price: finalPrice,
          currency: currencyName,
          item_ids: [product.shop_sku],
          item_category: 'eyewear',
        },
      };
    case ProviderType.SNAPCHAT_CONVERSION:
      return {
        eventName: 'VIEW_CONTENT',
        eventId,
        properties: {
          currency: currencyName,
          contents: [
            {
              id: product.shop_sku,
              quantity: 1,
              item_price: finalPrice,
            },
          ],
        },
      };
    case ProviderType.OPENAI: {
      const amount = toOpenAiMinorUnits(finalPrice);
      const money = amount === undefined ? {} : { amount, currency: currencyName };
      return {
        eventName: 'contents_viewed',
        eventId,
        properties: {
          type: 'contents',
          ...money,
          contents: [
            {
              id: product.shop_sku || String(product.id || ''),
              name: product.name || product.short_name || '',
              content_type: 'product',
              quantity: 1,
              ...money,
            },
          ],
        },
      };
    }
    case ProviderType.TIKTOK: {
      const resolvedTikTokEventId = resolveTrackingEventId(eventId);
      return {
        eventName: 'ViewContent',
        eventId: resolvedTikTokEventId,
        properties: {
          content_type: 'product',
          content_id: product.shop_sku,
          event_id: resolvedTikTokEventId,
          currency: currencyName,
          value: finalPrice,
          server_report_data: {
            currency: currencyName,
            contents: [
              {
                content_id: product.shop_sku,
                content_type: 'product',
                content_name: product.name,
              },
            ],
            value: finalPrice,
          },
        },
      };
    }
    // 跟运营张彪确认，英国和加拿大不发送 conversion 事件
    case ProviderType.GA4: {
      const isConversionEnabled = !['uk', 'ca'].includes(env.NEXT_PUBLIC_SITE_NAME);
      const events: unknown[] = [];
      if (isConversionEnabled) {
        events.push({
          eventName: 'conversion',
          properties: {
            send_to: `${env.NEXT_PUBLIC_GA4_AD_ID2}/${env.NEXT_PUBLIC_GA4_GOODS_DETAIL}`,
          },
        });
      }
      events.push({
        eventName: 'select_content',
        properties: {
          non_interaction: true,
          content_type: 'product',
          items: [
            {
              item_id: product.shop_sku,
              name: product.name,
              variant: product.product_frame_color_name,
              price: finalPrice,
            },
          ],
        },
      });
      // 如果有搜索索引，发送额外的 GA4 事件
      if (searchParams.search_index) {
        events.push({
          eventName: 'select_item',
          properties: {
            non_interaction: true,
            items: [
              {
                item_id: product.shop_sku,
                name: product.name,
                index: searchParams.search_index,
                page: searchParams.page,
              },
            ],
          },
        });
      }
      return events;
    }
    case ProviderType.APPLOVIN:
      return {
        eventName: 'view_item',
        eventId,
        properties: {
          currency: currencyName,
          items: [
            {
              item_id: product.spu,
              item_variant_id: product.shop_sku,
              image_url:
                (product.image.celebrity?.[0] as any)?.url ||
                (product.image.material?.[0] as any)?.url ||
                (product.image.product?.[0] as any)?.url ||
                '',
              item_name: product.short_name,
              item_category_id: '524',
              price: parseFloat(baseFinalPrice),
              quantity: 1,
            },
          ],
          value: parseFloat(baseFinalPrice),
        },
      };
    case ProviderType.RTB_HOUSE:
      return {
        eventName: 'offer',
        eventId,
        properties: {
          offerId: product.shop_sku,
        },
      };
    case ProviderType.SNOWPLOW:
      return {
        eventName: 'ProductView',
        eventId,
        properties: {
          shop_sku: product.shop_sku,
          product_id: String(product.id || ''),
          product_name: product.name || product.short_name || '',
          currency: currencyName,
          product_price: finalPrice,
        },
      };
    default:
      return null;
  }
}
